import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Play, Calendar, ExternalLink, Loader2, ArrowLeft } from 'lucide-react';
import api, { resolveImageUrl } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import Pagination from '../components/Pagination';

const RilisanPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [genre, setGenre] = useState('Semua');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    document.title = 'Semua Rilisan — Werdi Media';
    window.scrollTo(0, 0);
    api
      .get('/releases', { params: { limit: 500 } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const genres = useMemo(
    () => ['Semua', ...Array.from(new Set(items.map((r) => r.genre)))],
    [items]
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((r) => {
      const matchQ =
        !query ||
        r.title.toLowerCase().includes(query) ||
        r.artist.toLowerCase().includes(query) ||
        (r.genre || '').toLowerCase().includes(query);
      const matchG = genre === 'Semua' || r.genre === genre;
      return matchQ && matchG;
    });
  }, [items, q, genre]);

  useEffect(() => {
    setPage(1);
  }, [q, genre]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage]
  );

  const goTo = (p) => {
    setPage(p);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="App bg-[#0a0a0a] min-h-screen">
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 pb-12 lg:pt-40 lg:pb-16 overflow-hidden">
        <div className="absolute top-0 -left-32 w-[500px] h-[500px] rounded-full bg-[#D4A94F]/10 blur-[120px] -z-10" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-neutral-500 hover:text-[#E9C46A] link-transition mb-6"
          >
            <ArrowLeft className="w-3 h-3" /> Beranda
          </Link>
          <div className="inline-block px-4 py-1.5 rounded-full border border-[#D4A94F]/30 bg-[#D4A94F]/5 mb-5">
            <span className="text-xs font-medium tracking-widest uppercase text-[#E9C46A]">
              Katalog Rilisan
            </span>
          </div>
          <h1 className="text-white font-bold text-4xl md:text-6xl leading-[1.05] tracking-tight mb-4">
            Semua <span className="gold-gradient-text">Rilisan Musik</span>
          </h1>
          <p className="text-neutral-400 text-lg max-w-2xl mb-10">
            Jelajahi seluruh katalog rilisan musik dari artis-artis binaan Werdi Media.
          </p>

          {/* Search + filter */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
              <Input
                placeholder="Cari judul lagu, artis, atau genre..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-11 h-12 rounded-full bg-[#0f0f0f] border-white/10 focus-visible:ring-[#D4A94F] focus-visible:border-[#D4A94F] text-white"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {genres.map((g) => (
                <button
                  key={g}
                  onClick={() => setGenre(g)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold btn-transition border ${
                    genre === g
                      ? 'bg-[#D4A94F] text-black border-[#D4A94F]'
                      : 'bg-transparent text-neutral-300 border-white/10 hover:border-[#D4A94F]/50 hover:text-white'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="flex justify-center py-24">
              <Loader2 className="w-6 h-6 text-[#E9C46A] animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24 text-neutral-500">
              Tidak ada rilisan yang cocok. Coba kata kunci lain.
            </div>
          ) : (
            <>
              <p className="text-neutral-500 text-sm mb-6" data-testid="rilisan-count">
                Menampilkan {(currentPage - 1) * PAGE_SIZE + 1}
                {'–'}
                {Math.min(currentPage * PAGE_SIZE, filtered.length)} dari {filtered.length} rilisan
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5" data-testid="rilisan-grid">
                {paged.map((r) => (
                  <div
                    key={r.id}
                    className="group relative rounded-2xl overflow-hidden bg-[#111] border border-white/[0.06] hover:border-[#D4A94F]/40 card-transition"
                  >
                    <div className="aspect-square relative overflow-hidden bg-neutral-900">
                      {r.cover_image && (
                        <img
                          src={resolveImageUrl(r.cover_image)}
                          alt={r.title}
                          className="w-full h-full object-cover btn-transition group-hover:scale-105"
                          loading="lazy"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-70" />
                      <div className="absolute top-3 left-3">
                        <span className="text-[10px] tracking-widest uppercase font-bold text-black bg-[#D4A94F] px-2.5 py-1 rounded-full">
                          {r.genre}
                        </span>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 btn-transition">
                        <a
                          href={r.spotify_url || r.youtube_url || '#'}
                          target={r.spotify_url || r.youtube_url ? '_blank' : undefined}
                          rel="noreferrer"
                          className="w-14 h-14 rounded-full bg-[#D4A94F] flex items-center justify-center shadow-[0_10px_30px_-5px_rgba(212,169,79,0.6)] hover:scale-110 btn-transition"
                        >
                          {r.spotify_url || r.youtube_url ? (
                            <ExternalLink className="w-5 h-5 text-black" />
                          ) : (
                            <Play className="w-5 h-5 text-black fill-black ml-0.5" />
                          )}
                        </a>
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="text-white font-semibold text-base leading-tight truncate">
                        {r.title}
                      </h4>
                      <p className="text-neutral-400 text-sm truncate mt-0.5">{r.artist}</p>
                      <div className="flex items-center gap-1.5 mt-2 text-xs text-neutral-500">
                        <Calendar className="w-3 h-3" />
                        <span>{fmtDate(r.release_date)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={goTo}
                  testIdPrefix="rilisan"
                />
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RilisanPage;
